import { InformationCircleIcon, PencilIcon } from "@heroicons/react/24/outline";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import { useConfigurations } from "@via/api/configuration.tsx";
import CollapsibleContainer from "@via/components/collapsible-container/collapsible-container";
import { GenericError } from "@via/components/error";
import FormInputComponent from "@via/components/inputs/form-input-component";
import { SkeletonLoader } from "@via/components/loading/loading";
import { HeadingSize, PageHeading } from "@via/components/page-heading";
import SelectComponent from "@via/components/select/select.tsx";
import Table, { TableSize } from "@via/components/table/table.tsx";
import { NOTIFICATION } from "@via/context/notifications-context.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import {
  convertProcGnumToUnderscorePattern,
  convertUnderscoreToProcGnumPattern,
  isPipelineExternal,
} from "@via/pages/projects/runs/utils/helpers.tsx";
import { ERROR, ROUTES, RunTabs } from "@via/utils/constants";
import {
  ExecutorJobsType,
  type GetRunDetailsResponseBody,
  type RunEnvironmentAvailableListItem,
  type RunParameterListResponseBody,
} from "common";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type JSX,
  type ReactNode,
} from "react";
import { UseFormReturn } from "react-hook-form";
import { Tooltip } from "react-tooltip";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";
import { useParams } from "react-router-dom";

interface AdvancedProps {
  form: UseFormReturn;
  runSettingsData: GetRunDetailsResponseBody;
  pipelineRunDetails: RunParameterListResponseBody;
  isRunSettingsSuccess: boolean;
  isRunSettingsLoading: boolean;
  isRunSettingsError: boolean;
  readOnly: boolean;
  activeRunTab: RunTabs;
  /**
   * 3G-3a spec 5.3 (D13): an unsaved environment switch the run page holds. While set, offered by the fetched
   * list and different from the fetched selection, the refetch branch below must not put the fetched execution
   * directory back. settings.tsx applies the same rule to its own reset.
   */
  pendingRunEnvironmentId?: number | null;
}

const Advanced: FC<AdvancedProps> = ({
  form,
  runSettingsData,
  pipelineRunDetails,
  isRunSettingsSuccess,
  isRunSettingsLoading,
  isRunSettingsError,
  readOnly,
  activeRunTab,
  pendingRunEnvironmentId = null,
}) => {
  const { runId = "" } = useParams();
  const { setNotification } = useNotifications();
  const isSuccess = isRunSettingsSuccess;
  const isLoading = isRunSettingsLoading;
  const isError = isRunSettingsError;

  const [isEditingPublishDir, setIsEditingPublishDir] = useState(false);
  const [isEditingWorkDir, setIsEditingWorkDir] = useState(false);

  const { control, register, setValue, watch, getValues } = form;

  const { data: configurations } = useConfigurations();

  const useDocker = watch("useDocker");
  const useSingularity = watch("useSingularity");
  const publishDirPath = watch("publishDir");
  const workDirectoryPath = watch("workDirectory");
  const launchDirectoryPath = watch("launchDirectory");
  const execAllCheck = watch("execAllCheck");
  const execEachCheck = watch("execEachCheck");
  const deleteIntermediateCheck = watch("deleteIntermediateCheck");
  const selectedRunEnvironment: RunEnvironmentAvailableListItem | null = watch(
    "selectedRunEnvironment"
  );
  const selectedRunEnvironmentUpdated = watch("selectedRunEnvironmentUpdated"); // Refresh Run Environment Button
  const previousSelectedRunEnvironmentRef =
    useRef<RunEnvironmentAvailableListItem | null>(null);
  // Read through a ref inside the effect so its dependency list stays as it is: setting a pending selection
  // must never re-run the reset branch.
  const pendingRunEnvironmentIdRef = useRef<number | null>(
    pendingRunEnvironmentId
  );
  pendingRunEnvironmentIdRef.current = pendingRunEnvironmentId;

  const isExternalPipeline = useMemo(
    () => isPipelineExternal(runSettingsData.mainPipeline.pipelineType),
    [runSettingsData.mainPipeline.pipelineType]
  );

  const isProjectBucketLocked = Boolean(
    runSettingsData?.advancedSettings?.publishDir?.projectBucket
  );

  const hasSelectedRunEnvironment = Boolean(selectedRunEnvironment?.id);

  const normalizedPublishDirPath = useMemo(() => {
    return (publishDirPath ?? "").trim().replace(/\/+$/, "");
  }, [publishDirPath]);

  const normalizedWorkDirectoryPath = useMemo(() => {
    return (workDirectoryPath ?? "").trim().replace(/\/+$/, "");
  }, [workDirectoryPath]);

  const normalizedLaunchDirectoryPath = useMemo(() => {
    return (launchDirectoryPath ?? "").trim().replace(/\/+$/, "");
  }, [launchDirectoryPath]);

  const resolvedWorkDirectoryPath = useMemo(() => {
    const base =
      normalizedWorkDirectoryPath ||
      normalizedPublishDirPath ||
      normalizedLaunchDirectoryPath;
    return base ? `${base}/run${runId}/work` : "";
  }, [
    normalizedLaunchDirectoryPath,
    normalizedPublishDirPath,
    normalizedWorkDirectoryPath,
    runId,
  ]);

  const resolvedPublishDirectoryPath = useMemo(() => {
    const base = normalizedPublishDirPath || normalizedLaunchDirectoryPath;
    return base ? `${base}/report${runId}` : "";
  }, [normalizedLaunchDirectoryPath, normalizedPublishDirPath, runId]);

  useEffect(() => {
    // This useEffect hook will trigger either when selectedRunEnvironment value changes or when the component mount
    // and will set the values of the advanced form fields + Work Directory based on the fetched data or selectedRunEnvironment or autofill

    // On selectedRunEnvironment changes: Update the advanced form fields based on the new selectedRunEnvironment value and then apply autofill rules if necessary
    if (
      previousSelectedRunEnvironmentRef.current?.id !==
      selectedRunEnvironment?.id
    ) {
      if (selectedRunEnvironment) {
        updateValuesFromSelectedRunEnvironment();
        if (!getValues("updateAutofillAfterSelectedRunEnvironment")) {
          setValue("updateAutofillAfterSelectedRunEnvironment", true);
        }
      }
      // Update the previous value to the current one for future comparisons
      previousSelectedRunEnvironmentRef.current = selectedRunEnvironment;
    }
    // On initial mount: Allow the initial values to be set by the run settings response
    else if (isRunSettingsSuccess) {
      const { advancedSettings } = runSettingsData || {};

      // Clear all existing execEach form values
      const existingExecEachKeys = getValues("execEach") || {};
      Object.keys(existingExecEachKeys).forEach(key => {
        setValue(`execEach.${key}.check`, false);
        setValue(`execEach.${key}.queue`, "");
        setValue(`execEach.${key}.memory`, "");
        setValue(`execEach.${key}.cpu`, "");
        setValue(`execEach.${key}.time`, "");
        setValue(`execEach.${key}.options`, "");
      });

      // The execution directory belongs to the submission the user is about to make (D13): while the pending
      // switch is held and differs from the fetched selection, the local value must survive both the
      // environment defaults reset below and the fetched value that follows it. This branch only: the
      // environment-changed branch above is the autofill a manual environment pick gets, pending or not, and
      // must not be guarded, or a fallback switch to the agent environment would keep the SSH path.
      const pendingId = pendingRunEnvironmentIdRef.current;
      const availableList =
        runSettingsData?.runEnvironment?.availableList ?? [];
      const pendingHeld =
        pendingId !== null && availableList.some(({ id }) => id === pendingId);
      const pendingDiffers =
        pendingHeld &&
        pendingId !== (runSettingsData?.runEnvironment?.selectedId ?? null);
      const localLaunchDirectory = pendingDiffers
        ? getValues("launchDirectory")
        : undefined;

      // Even if this hook is not being triggered by the selectedRunEnvironment change, some of the missing fields in response should be filled once by the selectedRunEnvironment.
      // e.g. There is no memory or cpu information for the unselected process for each process in executer settings.
      //      These fields should be populated with the default values from selectedRunEnvironment.
      //      This is why we are calling updateValuesFromSelectedRunEnvironment here.
      // Rest of the fields will be overwritten with the values from the response.
      if (selectedRunEnvironment) {
        updateValuesFromSelectedRunEnvironment();
      }
      if (pendingDiffers) {
        setValue("launchDirectory", localLaunchDirectory);
      } else {
        setValue("launchDirectory", runSettingsData?.launchDirectory ?? "");
      }

      setValue("useDocker", advancedSettings?.docker?.enabled ?? false);
      setValue(
        "useSingularity",
        advancedSettings?.singularity?.enabled ?? false
      );

      setValue("nextflowVersion", advancedSettings?.nextflowVersion ?? "");

      setValue("dockerConfig.image", advancedSettings?.docker?.image ?? "");
      setValue(
        "dockerConfig.runOptions",
        advancedSettings?.docker?.options ?? ""
      );
      setValue(
        "singularityConfig.imagePath",
        advancedSettings?.singularity?.image ?? ""
      );
      setValue(
        "singularityConfig.runOptions",
        advancedSettings?.singularity?.options ?? ""
      );
      // If run belongs to project bucket, publishDir is forced to project bucket
      if (advancedSettings?.publishDir?.projectBucket) {
        const forced = advancedSettings.publishDir.projectBucket;
        setValue("publishDir", forced);
        setValue("workDirectory", "");
      } else {
        const publish = advancedSettings?.publishDir?.path ?? "";
        const work = advancedSettings?.workDir?.workDirectory ?? "";
        setValue("publishDir", publish);
        setValue("workDirectory", work);
      }
      setIsEditingPublishDir(
        Boolean(
          !advancedSettings?.publishDir?.projectBucket &&
          (advancedSettings?.publishDir?.path ?? "").trim()
        )
      );
      setIsEditingWorkDir(
        Boolean(
          !advancedSettings?.publishDir?.projectBucket &&
          (advancedSettings?.workDir?.workDirectory ?? "").trim()
        )
      );
      setValue("execAllCheck", advancedSettings?.globalExec?.enabled ?? false);
      setValue(
        "execAll.queue",
        advancedSettings?.globalExec?.options?.job_queue ?? ""
      );
      setValue(
        "execAll.memory",
        advancedSettings?.globalExec?.options?.job_memory ?? ""
      );
      setValue(
        "execAll.cpu",
        advancedSettings?.globalExec?.options?.job_cpu ?? ""
      );
      setValue(
        "execAll.time",
        advancedSettings?.globalExec?.options?.job_time ?? ""
      );
      setValue(
        "execAll.options",
        advancedSettings?.globalExec?.options?.job_clu_opt ?? ""
      );
      setValue(
        "execEachCheck",
        advancedSettings?.processExec?.enabled ?? false
      );

      if (advancedSettings?.processExec?.options) {
        Object.entries(advancedSettings?.processExec?.options).forEach(
          ([key, value]) => {
            setValue(
              `execEach.${key}.check`,
              (value as Record<string, unknown>)?.check === "on" ||
                (value as Record<string, unknown>)?.check === true
            );
            setValue(
              `execEach.${key}.queue`,
              (value as Record<string, unknown>)?.queue ?? ""
            );
            setValue(
              `execEach.${key}.memory`,
              (value as Record<string, unknown>)?.memory ?? ""
            );
            setValue(
              `execEach.${key}.cpu`,
              (value as Record<string, unknown>)?.cpu ?? ""
            );
            setValue(
              `execEach.${key}.time`,
              (value as Record<string, unknown>)?.time ?? ""
            );
            setValue(
              `execEach.${key}.options`,
              (value as Record<string, unknown>)?.opt ?? ""
            );
          }
        );
      }
      setValue(
        "deleteIntermediateCheck",
        advancedSettings?.deleteIntermediateFiles?.enabled ?? true
      );
      setValue(
        "deleteIntermediateDelay",
        advancedSettings?.deleteIntermediateFiles?.delayInHours ?? 120
      );
      setValue(
        "reportOptions",
        advancedSettings?.reportOptions ?? {
          report: true,
          trace: true,
          dag: false,
          timeline: true,
        }
      );
    }
  }, [selectedRunEnvironment, isRunSettingsSuccess, runSettingsData, setValue]);

  useEffect(() => {
    if (isError) {
      setNotification({
        id: "unable-to-access-run",
        message: "Unable to Access Run",
        details: "Run is not found or you do not have permission to access",
        variant: NOTIFICATION.ERROR,
      });
    }
  }, [isError]);

  useEffect(() => {
    if (useDocker) {
      setValue("useSingularity", false);
    }
  }, [useDocker]);

  useEffect(() => {
    if (useSingularity) {
      setValue("useDocker", false);
    }
  }, [useSingularity]);

  const updateExecValues = (key, value) => {
    setValue(`execAll.${key}`, value);
    if (!pipelineRunDetails?.inputs?.processes) {
      return;
    }
    Object.keys(pipelineRunDetails.inputs.processes).forEach(processId => {
      setValue(
        `execEach.${convertUnderscoreToProcGnumPattern(processId)}.${key}`,
        value
      );
    });
  };

  const updateValuesFromSelectedRunEnvironment = () => {
    setValue("launchDirectory", selectedRunEnvironment?.defWorkdir ?? "");
    // If run belongs to project bucket, don't allow selectedRunEnvironment to override publishDir
    if (!runSettingsData?.advancedSettings?.publishDir?.projectBucket) {
      setValue("publishDir", selectedRunEnvironment?.defPublishdir ?? "");
    }
    // Clear directory overrides on run environment change
    setValue("workDirectory", selectedRunEnvironment?.defNextflowWorkdir ?? "");
    setIsEditingPublishDir(false);
    setIsEditingWorkDir(false);

    if (selectedRunEnvironment?.jobQueue) {
      updateExecValues("queue", selectedRunEnvironment.jobQueue);
    }

    if (selectedRunEnvironment?.jobMemory) {
      updateExecValues("memory", selectedRunEnvironment.jobMemory);
    }

    if (selectedRunEnvironment?.jobCpu) {
      updateExecValues("cpu", selectedRunEnvironment.jobCpu);
    }

    if (selectedRunEnvironment?.jobTime) {
      updateExecValues("time", selectedRunEnvironment.jobTime);
    }

    // Always overwrite cluster options on environment change — including clearing
    // them when the new environment defines none. The previous `if (jobCluOpt)`
    // guard left the prior environment's options in place when switching to one
    // with none (e.g. moving off the old GHPCC cluster, whose `-R "select[rh=8]"`
    // then leaks onto a cluster that rejects it). Mirrors the directory resets above.
    updateExecValues("options", selectedRunEnvironment?.jobCluOpt ?? "");

    // Only set container type from selectedRunEnvironment if it has changed
    if (selectedRunEnvironment?.containerType) {
      setValue("useDocker", selectedRunEnvironment.containerType === "docker");
      setValue(
        "useSingularity",
        selectedRunEnvironment.containerType === "singularity"
      );
    }
  };

  useEffect(() => {
    // This useEffect hook will run only when refresh environment button is clicked
    // and will update the advanced form fields + Work Directory based on the selectedRunEnvironment value then apply autofill rules if necessary

    if (selectedRunEnvironmentUpdated) {
      if (selectedRunEnvironment) {
        updateValuesFromSelectedRunEnvironment();
        if (!getValues("updateAutofillAfterSelectedRunEnvironment")) {
          setValue("updateAutofillAfterSelectedRunEnvironment", true);
        }
      }
    }
  }, [selectedRunEnvironmentUpdated]);

  const renderCheckboxWithTooltip = (
    name: string,
    label: string,
    tooltipText: ReactNode | string,
    disabled?: boolean
  ): JSX.Element => (
    <label className="align-items flex items-center">
      <input
        {...register(name)}
        type="checkbox"
        className="mr-2 block h-6 w-6 rounded-md border-0 text-indigo-600 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
        disabled={readOnly || disabled}
      />
      <PageHeading title={label} size={HeadingSize.XSmall} />
      <InformationCircleIcon id={`${name}-check`} className="mx-2 h-4" />
      <Tooltip
        anchorSelect={`#${name}-check`}
        offset={6}
        place="right"
        render={() => <>{tooltipText}</>}
      />
    </label>
  );

  const renderInputComponent = (id): JSX.Element => (
    <input
      {...register(id)}
      type="text"
      onChange={e => {
        setValue(id, e.target.value);

        // Ensure only fields starting with "execEach" trigger the checkbox update
        if (id.startsWith("execEach")) {
          const checkField = id.replace(
            /\.(queue|memory|cpu|time|options)$/,
            ".check"
          );
          setValue(checkField, true);
        }
      }}
      className="mr-3 block w-[95%] rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
      disabled={readOnly}
    />
  );

  const renderCheckboxComponent = (id): JSX.Element => (
    <FormInputComponent
      key={`id-${watch(id)}`} // force re-render when value changes
      control={control}
      name={id}
      type="checkbox"
      disabled={readOnly}
      className="h-6 w-6 mr-2 -mt-2"
    />
  );

  const columnHelper = createColumnHelper<{ name: string }>();
  const globalColumns = useMemo(() => {
    return [
      ...(!(
        selectedRunEnvironment?.executorJob === ExecutorJobsType.Local ||
        selectedRunEnvironment?.executorJob === ExecutorJobsType.Ignite ||
        selectedRunEnvironment?.executorJob === ExecutorJobsType.GoogleBatch
      )
        ? [
            columnHelper.accessor("name", {
              id: "queue",
              cell: () => <div>{renderInputComponent("execAll.queue")}</div>,
              header:
                selectedRunEnvironment?.executorJob === ExecutorJobsType.Slurm
                  ? "Partition"
                  : "Queue",
            }),
          ]
        : []),
      columnHelper.accessor("name", {
        id: "memory",
        cell: () => <div>{renderInputComponent("execAll.memory")}</div>,
        header: "Memory (GB)",
      }),
      columnHelper.accessor("name", {
        id: "cpu",
        cell: () => <div>{renderInputComponent("execAll.cpu")}</div>,
        header: "CPUs",
      }),
      ...(!(
        selectedRunEnvironment?.executorJob === ExecutorJobsType.Local ||
        selectedRunEnvironment?.executorJob === ExecutorJobsType.Ignite
      )
        ? [
            columnHelper.accessor("name", {
              id: "time",
              cell: () => <div>{renderInputComponent("execAll.time")}</div>,
              header: "Time (min.)",
            }),
          ]
        : []),
      ...(!(
        selectedRunEnvironment?.executorJob === ExecutorJobsType.Local ||
        selectedRunEnvironment?.executorJob === ExecutorJobsType.GoogleBatch
      )
        ? [
            columnHelper.accessor("name", {
              id: "options",
              cell: () => <div>{renderInputComponent("execAll.options")}</div>,
              header: "Other Options",
            }),
          ]
        : []),
    ] as Array<ColumnDef<{ name: string }>>;
  }, [selectedRunEnvironment, readOnly]);

  const processColumns = useMemo(() => {
    return [
      columnHelper.accessor("name", {
        id: "select",
        cell: info => (
          <div>
            {renderCheckboxComponent(`execEach.${info.getValue()}.check`)}
          </div>
        ),
        header: "Select",
        size: 50,
      }),
      columnHelper.accessor("name", {
        id: "processName",
        cell: info =>
          pipelineRunDetails?.inputs?.processes?.[
            convertProcGnumToUnderscorePattern(info.getValue())
          ]?.details?.longVarName ?? info.getValue(),
        header: "Process Name",
      }),
      ...(!(
        selectedRunEnvironment?.executorJob === ExecutorJobsType.Local ||
        selectedRunEnvironment?.executorJob === ExecutorJobsType.Ignite ||
        selectedRunEnvironment?.executorJob === ExecutorJobsType.GoogleBatch
      )
        ? [
            columnHelper.accessor("name", {
              id: "queue",
              cell: info => (
                <div>
                  {renderInputComponent(`execEach.${info.getValue()}.queue`)}
                </div>
              ),
              header:
                selectedRunEnvironment?.executorJob === ExecutorJobsType.Slurm
                  ? "Partition"
                  : "Queue",
              size: 120,
            }),
          ]
        : []),
      columnHelper.accessor("name", {
        id: "memory",
        cell: info => (
          <div>
            {renderInputComponent(`execEach.${info.getValue()}.memory`)}
          </div>
        ),
        header: "Memory (GB)",
        size: 50,
      }),
      columnHelper.accessor("name", {
        id: "cpu",
        cell: info => (
          <div>{renderInputComponent(`execEach.${info.getValue()}.cpu`)}</div>
        ),
        header: "CPUs",
        size: 50,
      }),
      ...(!(
        selectedRunEnvironment?.executorJob === ExecutorJobsType.Local ||
        selectedRunEnvironment?.executorJob === ExecutorJobsType.Ignite
      )
        ? [
            columnHelper.accessor("name", {
              id: "time",
              cell: info => (
                <div>
                  {renderInputComponent(`execEach.${info.getValue()}.time`)}
                </div>
              ),
              header: "Time (min.)",
              size: 50,
            }),
          ]
        : []),
      ...(!(
        selectedRunEnvironment?.executorJob === ExecutorJobsType.Local ||
        selectedRunEnvironment?.executorJob === ExecutorJobsType.GoogleBatch
      )
        ? [
            columnHelper.accessor("name", {
              id: "options",
              cell: info => (
                <div>
                  {renderInputComponent(`execEach.${info.getValue()}.options`)}
                </div>
              ),
              header: "Other Options",
              size: 50,
            }),
          ]
        : []),
    ] as Array<ColumnDef<{ name: string }>>;
  }, [pipelineRunDetails, selectedRunEnvironment, readOnly]);

  const getRunAdvancedContext = useCallback(() => {
    const advancedSettings = runSettingsData?.advancedSettings || {};
    const execEach = getValues("execEach") || {};
    const execAll = getValues("execAll") || {};

    return {
      run: {
        name: runSettingsData?.name,
        runUUID: runSettingsData?.runUUID,
        owner: runSettingsData?.owner?.username,
        own: runSettingsData?.owner?.own,
      },
      pipeline: {
        id: runSettingsData?.mainPipeline?.id,
        name: runSettingsData?.mainPipeline?.name,
        version: runSettingsData?.mainPipeline?.version,
        pipelineType: runSettingsData?.mainPipeline?.pipelineType,
        isExternal: isExternalPipeline,
      },
      advancedSettings: {
        docker: {
          enabled: useDocker || false,
          image:
            getValues("dockerConfig.image") || advancedSettings?.docker?.image,
          options:
            getValues("dockerConfig.runOptions") ||
            advancedSettings?.docker?.options,
        },
        singularity: {
          enabled: useSingularity || false,
          image:
            getValues("singularityConfig.imagePath") ||
            advancedSettings?.singularity?.image,
          options:
            getValues("singularityConfig.runOptions") ||
            advancedSettings?.singularity?.options,
        },
        workDir: {
          workDirectory:
            getValues("workDirectory") ||
            advancedSettings?.workDir?.workDirectory,
          resolvedWorkPath: advancedSettings?.workDir?.resolvedWorkPath,
        },
        publishDir: {
          enabled: Boolean(
            getValues("publishDir") || advancedSettings?.publishDir?.path
          ),
          path: getValues("publishDir") || advancedSettings?.publishDir?.path,
          projectBucket: advancedSettings?.publishDir?.projectBucket,
          resolvedReportPath: advancedSettings?.publishDir?.resolvedReportPath,
        },
        globalExec: {
          enabled: execAllCheck || false,
          queue:
            execAll.queue || advancedSettings?.globalExec?.options?.job_queue,
          memory:
            execAll.memory || advancedSettings?.globalExec?.options?.job_memory,
          cpu: execAll.cpu || advancedSettings?.globalExec?.options?.job_cpu,
          time: execAll.time || advancedSettings?.globalExec?.options?.job_time,
          options:
            execAll.options ||
            advancedSettings?.globalExec?.options?.job_clu_opt,
        },
        processExec: {
          enabled: execEachCheck || false,
          processes: Object.keys(execEach)
            .slice(0, 20)
            .map(key => ({
              processId: key,
              check: execEach[key]?.check || false,
              queue: execEach[key]?.queue || "",
              memory: execEach[key]?.memory || "",
              cpu: execEach[key]?.cpu || "",
              time: execEach[key]?.time || "",
              options: execEach[key]?.options || "",
            })),
        },
        deleteIntermediateFiles: {
          enabled:
            deleteIntermediateCheck !== undefined
              ? deleteIntermediateCheck
              : (advancedSettings?.deleteIntermediateFiles?.enabled ?? true),
          delayInHours:
            getValues("deleteIntermediateDelay") ||
            advancedSettings?.deleteIntermediateFiles?.delayInHours ||
            120,
        },
        reportOptions: getValues("reportOptions") ||
          advancedSettings?.reportOptions || {
            report: true,
            trace: true,
            dag: false,
            timeline: true,
          },
      },
      runEnvironment: {
        id: selectedRunEnvironment?.id,
        name: selectedRunEnvironment?.name,
        executorJob: selectedRunEnvironment?.executorJob,
      },
      readOnly: readOnly,
    };
  }, [
    runSettingsData,
    isExternalPipeline,
    useDocker,
    useSingularity,
    execAllCheck,
    execEachCheck,
    deleteIntermediateCheck,
    selectedRunEnvironment,
    readOnly,
    getValues,
  ]);

  useRegisterCortexContext({
    routePattern: ROUTES.RUN_DETAILS,
    contextName: "Run Advanced Settings",
    getContext: getRunAdvancedContext,
    systemPrompt:
      "You are analyzing run advanced settings page. Help answer questions about Docker/Singularity configuration, executor settings, publish directory, process-specific executor settings, and other advanced run configurations.",
    enabled:
      !!runSettingsData &&
      isRunSettingsSuccess &&
      activeRunTab === RunTabs.Advanced,
  });

  return (
    <main className="py-4">
      {isLoading ? (
        <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
          <SkeletonLoader count={12} />
        </div>
      ) : isError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : isSuccess ? (
        <>
          <div className="mb-6">
            <PageHeading title="Nextflow Version" size={HeadingSize.XSmall} />
            <SelectComponent
              control={control}
              name="nextflowVersion"
              placeholder="Default (recommended)"
              options={[
                { label: "Default (recommended)", value: "" },
                ...(configurations?.nextflowVersions ?? []).map(v => ({
                  label: v,
                  value: v,
                })),
              ]}
              readOnly={readOnly}
              enableClear={false}
            />
            <p className="mt-1 text-sm text-gray-500">
              Overrides the pipeline&apos;s configured version. Leave as Default
              to use the pipeline&apos;s own setting.
            </p>
          </div>
          {!isExternalPipeline && (
            <div className="flex flex-col pb-2 text-sm leading-6 text-slate-600 md:pb-3">
              <div className="flex items-center">
                <PageHeading title="Run Container" size={HeadingSize.XSmall} />
                <InformationCircleIcon
                  id="run-container"
                  className="mx-2 h-4"
                />
                <Tooltip
                  anchorSelect="#run-container"
                  offset={6}
                  place="right"
                  render={() => <>Pipeline Container</>}
                />
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="align-items flex">
                    <input
                      {...register("useDocker")}
                      type="checkbox"
                      className="mr-2 block h-6 w-6 rounded-md border-0 text-indigo-600 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      disabled={readOnly}
                    />
                    <div className="block text-sm font-medium leading-6 text-gray-900">
                      Use Docker Image
                    </div>
                  </label>
                  {useDocker && (
                    <>
                      <div className="flex flex-col py-2 text-sm leading-6 text-slate-600 md:py-3">
                        <label className="flex flex-row items-center text-sm font-medium leading-6 text-gray-900">
                          Image
                        </label>
                        <div className="mt-2">
                          <input
                            {...register("dockerConfig.image")}
                            placeholder="Enter docker image"
                            type="text"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            disabled={readOnly}
                          />
                        </div>
                      </div>
                      <div className="flex flex-col py-2 text-sm leading-6 text-slate-600 md:py-3">
                        <label className="flex flex-row items-center text-sm font-medium leading-6 text-gray-900">
                          Run Options
                        </label>
                        <div className="mt-2">
                          <input
                            {...register("dockerConfig.runOptions")}
                            placeholder="Enter docker run options"
                            type="text"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            disabled={readOnly}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div>
                  <label className="align-items flex">
                    <input
                      {...register("useSingularity")}
                      type="checkbox"
                      className="mr-2 block h-6 w-6 rounded-md border-0 text-indigo-600 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      disabled={readOnly}
                    />
                    <div className="block text-sm font-medium leading-6 text-gray-900">
                      Use Singularity Image
                    </div>
                  </label>
                  {useSingularity && (
                    <>
                      <div className="flex flex-col py-2 text-sm leading-6 text-slate-600 md:py-3">
                        <label className="flex flex-row items-center text-sm font-medium leading-6 text-gray-900">
                          Image Path{" "}
                          <InformationCircleIcon
                            id="singularity-image-path"
                            className="mx-2 h-4"
                          />
                        </label>
                        <div className="mt-2">
                          <input
                            {...register("singularityConfig.imagePath")}
                            placeholder="Enter singularity image path"
                            type="text"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            disabled={readOnly}
                          />
                        </div>
                        <Tooltip
                          anchorSelect="#singularity-image-path"
                          offset={6}
                          place="right"
                          render={() => (
                            <>
                              (eg. project/umw_biocore/singularity/
                              UMMS-Biocore-singularity-master.simg)
                            </>
                          )}
                        />
                      </div>
                      <div className="flex flex-col py-2 text-sm leading-6 text-slate-600 md:py-3">
                        <label className="flex flex-row items-center text-sm font-medium leading-6 text-gray-900">
                          Run Options{" "}
                          <InformationCircleIcon
                            id="singularity-run-options"
                            className="mx-2 h-4"
                          />
                        </label>
                        <div className="mt-2">
                          <input
                            {...register("singularityConfig.runOptions")}
                            placeholder="Enter singularity run options"
                            type="text"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            disabled={readOnly}
                          />
                        </div>
                        <Tooltip
                          anchorSelect="#singularity-run-options"
                          offset={6}
                          place="right"
                          render={() => (
                            <>
                              You can mount the directories by using --bind
                              command (e.g., --bind /project:/project --bind
                              /nl:/nl --bind /share:/share). It requires you to
                              create the directories in the image beforehand.
                            </>
                          )}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="flex flex-col py-2 text-sm leading-6 text-slate-600 md:py-3">
            <div className="align-items flex items-center">
              <PageHeading title="Storage" size={HeadingSize.XSmall} />
              <InformationCircleIcon id="storage-info" className="mx-2 h-4" />
              <Tooltip
                anchorSelect="#storage-info"
                offset={6}
                place="right"
                render={() => (
                  <>
                    Foundry stores intermediate Nextflow files in the Work
                    Directory, and writes the final report output to the Publish
                    Directory.
                  </>
                )}
              />
            </div>

            {/* Keep form values even when inputs are read-only */}
            <input type="hidden" {...register("publishDir")} />
            <input type="hidden" {...register("workDirectory")} />

            <div className="mt-2 space-y-6">
              {/* Publish Directory */}
              <div>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-sm font-medium leading-6 text-gray-900">
                    Publish Directory
                    <InformationCircleIcon
                      id="publish-directory-info"
                      className="h-4 w-4 text-slate-600"
                    />
                    <Tooltip
                      anchorSelect="#publish-directory-info"
                      offset={6}
                      place="right"
                      render={() => (
                        <>
                          Foundry sets{" "}
                          <span className="font-mono">params.outdir</span> to
                          the Publish Directory. The final reports will be
                          written under this location.
                        </>
                      )}
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    {!!normalizedPublishDirPath &&
                    hasSelectedRunEnvironment &&
                    !isProjectBucketLocked ? (
                      <button
                        type="button"
                        className="text-xs font-medium text-gray-500 hover:text-gray-600"
                        disabled={readOnly}
                        onClick={() => {
                          setValue("publishDir", "", {
                            shouldDirty: true,
                          });
                          setIsEditingPublishDir(false);
                        }}>
                        Reset
                      </button>
                    ) : null}
                    {!isEditingPublishDir &&
                      !normalizedPublishDirPath &&
                      hasSelectedRunEnvironment &&
                      !isProjectBucketLocked && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 text-xs font-medium text-gray-500 hover:text-gray-600"
                          disabled={readOnly}
                          onClick={() => {
                            const next = normalizedLaunchDirectoryPath;
                            setValue("publishDir", next, {
                              shouldDirty: true,
                            });
                            setIsEditingPublishDir(true);
                          }}>
                          <PencilIcon className="h-4 w-4" />
                          Edit
                        </button>
                      )}
                  </div>
                </div>

                {isEditingPublishDir || normalizedPublishDirPath ? (
                  <>
                    <div className="mt-1 flex">
                      <input
                        value={getValues("publishDir") ?? ""}
                        onChange={e => {
                          setValue("publishDir", e.target.value, {
                            shouldDirty: true,
                          });
                        }}
                        placeholder="Enter base directory"
                        type="text"
                        className="block w-full rounded-l-md rounded-r-none border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        disabled={readOnly || isProjectBucketLocked}
                      />
                      <div className="inline-flex items-center rounded-r-md bg-gray-50 px-3 text-gray-400 shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm">
                        {`/report${runId}`}
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Final report will be available in:{" "}
                      <span className="break-all font-mono">
                        {resolvedPublishDirectoryPath}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="mt-1 break-all font-mono text-gray-500 sm:text-sm">
                    {resolvedPublishDirectoryPath}
                  </div>
                )}

                {/* intentionally no help text */}
              </div>

              {/* Work Directory */}
              <div>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-sm font-medium leading-6 text-gray-900">
                    Work Directory
                    <InformationCircleIcon
                      id="work-directory-info"
                      className="h-4 w-4 text-slate-600"
                    />
                    <Tooltip
                      anchorSelect="#work-directory-info"
                      offset={6}
                      place="right"
                      render={() => (
                        <>
                          Foundry stores intermediate run files in the Work
                          Directory. These files are deleted after the run
                          completes based on the Delete Intermediate Files After
                          Run setting.
                        </>
                      )}
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    {!!normalizedWorkDirectoryPath &&
                    hasSelectedRunEnvironment &&
                    !isProjectBucketLocked ? (
                      <button
                        type="button"
                        className="text-xs font-medium text-gray-500 hover:text-gray-600"
                        disabled={readOnly}
                        onClick={() => {
                          setValue("workDirectory", "", {
                            shouldDirty: true,
                          });
                          setIsEditingWorkDir(false);
                        }}>
                        Reset
                      </button>
                    ) : null}
                    {!isEditingWorkDir &&
                      !normalizedWorkDirectoryPath &&
                      hasSelectedRunEnvironment &&
                      !isProjectBucketLocked && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 text-xs font-medium text-gray-500 hover:text-gray-600"
                          disabled={readOnly}
                          onClick={() => {
                            const next =
                              normalizedPublishDirPath ||
                              normalizedLaunchDirectoryPath;
                            setValue("workDirectory", next, {
                              shouldDirty: true,
                            });
                            setIsEditingWorkDir(true);
                          }}>
                          <PencilIcon className="h-4 w-4" />
                          Edit
                        </button>
                      )}
                  </div>
                </div>

                {isEditingWorkDir || normalizedWorkDirectoryPath ? (
                  <>
                    <div className="mt-1 flex">
                      <input
                        value={getValues("workDirectory") ?? ""}
                        onChange={e => {
                          setValue("workDirectory", e.target.value, {
                            shouldDirty: true,
                          });
                        }}
                        placeholder="Optional: enter base work directory"
                        type="text"
                        className="block w-full rounded-l-md rounded-r-none border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        disabled={readOnly || isProjectBucketLocked}
                      />
                      <div className="inline-flex items-center rounded-r-md bg-gray-50 px-3 text-gray-400 shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm">
                        {`/run${runId}/work`}
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Intermediate run files will be located in:{" "}
                      <span className="break-all font-mono">
                        {resolvedWorkDirectoryPath}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="mt-1 break-all font-mono text-gray-500 sm:text-sm">
                    {resolvedWorkDirectoryPath}
                  </div>
                )}

                {/* intentionally no help text */}

                {!!runSettingsData?.advancedSettings?.publishDir
                  ?.projectBucket && (
                  <div className="mt-2 text-xs text-gray-500">
                    Work directory is disabled when a project bucket is
                    assigned.
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col py-2 text-sm leading-6 text-slate-600 md:py-3">
            {renderCheckboxWithTooltip(
              "execAllCheck",
              "Executor Settings for All Processes",
              "You may adjust the nextflow executor settings by clicking this item."
            )}
            {execAllCheck && (
              <Table
                data={[
                  {
                    name: "global",
                  },
                ]}
                enableSorting={false}
                columns={globalColumns}
                showPagination={false}
                pageSize={1}
                size={TableSize.Small}
                applyColumnSize={true}
              />
            )}
          </div>
          {!isExternalPipeline && (
            <div className="flex flex-col py-2 text-sm leading-6 text-slate-600 md:py-3">
              {renderCheckboxWithTooltip(
                "execEachCheck",
                "Executor Settings for Each Process",
                <>
                  You may change executor settings for each process and override
                  to &apos;executor settings for all processes&apos; by clicking
                  this item and clicking the <br />
                  checkbox of process that you want to change. This will only
                  affect the settings of clicked process and keep the original
                  settings for the rest.
                </>
              )}
              {execEachCheck && (
                <Table
                  data={
                    Object.keys(pipelineRunDetails.inputs?.processes ?? {}).map(
                      processId => ({
                        name: convertUnderscoreToProcGnumPattern(processId),
                      })
                    ) ?? []
                  }
                  enableSorting={false}
                  columns={processColumns}
                  showPagination={false}
                  pageSize={
                    Object.keys(pipelineRunDetails.inputs?.processes ?? {})
                      .length
                  }
                  size={TableSize.Small}
                  applyColumnSize={true}
                />
              )}
            </div>
          )}
          <div className="flex flex-col py-2 text-sm leading-6 text-slate-600 md:py-3">
            {renderCheckboxWithTooltip(
              "deleteIntermediateCheck",
              "Delete intermediate files after run",
              "You may add a delay for cleanup step for intermediate files"
            )}
            {deleteIntermediateCheck && (
              <FormInputComponent
                control={control}
                name="deleteIntermediateDelay"
                label="Intermediate Files Cleanup Delay (Hours)"
                disabled={readOnly}
              />
            )}
          </div>
          <CollapsibleContainer
            title="Report Options"
            size={HeadingSize.XSmall}>
            <div className="grid grid-cols-2 gap-6">
              <div className="flex justify-between">
                {["report", "trace", "dag", "timeline"].map(option => (
                  <label key={option} className="align-items flex">
                    <FormInputComponent
                      control={control}
                      name={`reportOptions.${option}`}
                      type="checkbox"
                      disabled={option === "trace" || readOnly}
                      className="h-6 w-6 mr-2 -mt-2"
                    />
                    <div className="block text-sm font-medium leading-6 text-gray-900">
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </CollapsibleContainer>
        </>
      ) : null}
    </main>
  );
};

export default Advanced;
